<template>
    <div class="pagination-container" v-if="totalPages > 0">
        <div class="pagination-info">
            <span class="text-sm text-gray-600">
                총 {{ totalCount }}개 중 {{ startItem }}~{{ endItem }}개 표시
            </span>
            <select 
                v-model="selectedPageSize" 
                @change="onPageSizeChange"
                class="page-size-select ml-4"
            >
                <option value="25">25개씩</option>
                <option value="50">50개씩</option>
                <option value="100">100개씩</option>
            </select>
        </div>
        
        <div class="pagination-controls">
            <!-- 첫 페이지 -->
            <vs-button 
                :disabled="currentPage === 1 || isLoading"
                @click="$emit('page-change', 1)"
                size="small"
                color="primary"
                type="line"
            >
                첫페이지
            </vs-button>
            
            <!-- 이전 페이지 -->
            <vs-button 
                :disabled="currentPage === 1 || isLoading"
                @click="$emit('page-change', currentPage - 1)"
                size="small"
                color="primary"
                type="line"
                icon-pack="feather"
                icon="icon-chevron-left"
            >
            </vs-button>
            
            <!-- 페이지 번호들 -->
            <div class="page-numbers">
                <vs-button
                    v-for="page in visiblePages"
                    :key="page"
                    :class="{ 'active-page': page === currentPage }"
                    :disabled="isLoading"
                    @click="$emit('page-change', page)"
                    size="small"
                    :color="page === currentPage ? 'primary' : 'dark'"
                    :type="page === currentPage ? 'filled' : 'line'"
                    class="page-button"
                >
                    {{ page }}
                </vs-button>
            </div>
            
            <!-- 다음 페이지 -->
            <vs-button 
                :disabled="currentPage === totalPages || isLoading"
                @click="$emit('page-change', currentPage + 1)"
                size="small"
                color="primary"
                type="line"
                icon-pack="feather"
                icon="icon-chevron-right"
            >
            </vs-button>
            
            <!-- 마지막 페이지 -->
            <vs-button 
                :disabled="currentPage === totalPages || isLoading"
                @click="$emit('page-change', totalPages)"
                size="small"
                color="primary"
                type="line"
            >
                마지막
            </vs-button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'TodoPagination',
    props: {
        currentPage: {
            type: Number,
            default: 1
        },
        totalPages: {
            type: Number,
            default: 0
        },
        totalCount: {
            type: Number,
            default: 0
        },
        pageSize: {
            type: Number,
            default: 50
        },
        isLoading: {
            type: Boolean,
            default: false
        }
    },
    emits: ['page-change', 'page-size-change'],
    data() {
        return {
            selectedPageSize: this.pageSize
        };
    },
    computed: {
        /**
         * 현재 페이지에서 표시되는 첫 번째 아이템 번호
         */
        startItem() {
            return (this.currentPage - 1) * this.pageSize + 1;
        },
        
        /**
         * 현재 페이지에서 표시되는 마지막 아이템 번호
         */
        endItem() {
            const end = this.currentPage * this.pageSize;
            return end > this.totalCount ? this.totalCount : end;
        },
        
        /**
         * 표시할 페이지 번호들 계산
         */
        visiblePages() {
            const pages = [];
            const maxVisiblePages = 5;
            
            if (this.totalPages <= maxVisiblePages) {
                // 총 페이지가 5개 이하면 모든 페이지 표시
                for (let i = 1; i <= this.totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // 현재 페이지를 중심으로 5개 페이지 표시
                let start = Math.max(1, this.currentPage - 2);
                let end = Math.min(this.totalPages, start + maxVisiblePages - 1);
                
                // 끝에 도달하면 시작점 조정
                if (end === this.totalPages) {
                    start = Math.max(1, end - maxVisiblePages + 1);
                }
                
                for (let i = start; i <= end; i++) {
                    pages.push(i);
                }
            }
            
            return pages;
        }
    },
    watch: {
        pageSize(newVal) {
            this.selectedPageSize = newVal;
        }
    },
    methods: {
        onPageSizeChange() {
            this.$emit('page-size-change', parseInt(this.selectedPageSize));
        }
    }
};
</script>

<style scoped>
.pagination-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background-color: #f8f9fa;
    border-top: 1px solid #dee2e6;
}

.pagination-info {
    display: flex;
    align-items: center;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;
}

.page-numbers {
    display: flex;
    gap: 4px;
}

.page-button {
    min-width: 36px;
}

.page-size-select {
    padding: 4px 8px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    background-color: white;
    font-size: 14px;
}

.page-size-select:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.text-sm {
    font-size: 0.875rem;
}

.text-gray-600 {
    color: #6b7280;
}

.ml-4 {
    margin-left: 1rem;
}

@media (max-width: 768px) {
    .pagination-container {
        flex-direction: column;
        gap: 12px;
    }
    
    .page-numbers {
        flex-wrap: wrap;
        justify-content: center;
    }
}
</style>